import { INSPECTION_ITEM_DEFAULT_NAMES } from '@editor/config/constants'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { zeroPad } from '@editor/services/Util'

export default class InspectionItemCollection {
  items: (Omit<InspectionItem, 'part_name'> & NonNullable<Pick<InspectionItem, 'part_name'>>)[]

  constructor(items: InspectionItem[]) {
    // Create a shallow copy of each item so we don't mess with the original references.
    // No need for deep copy as we are only modifying top-level properties.
    this.items = items.map((item) => ({ ...item }))
    this.setDefaultNames()
  }

  /**
   * Set default names for inspection items that lack a name.
   * Generally in the format of `<Type> <Number>`.
   *
   * The number is determined by the order of items in the array for each type.
   * By default, numbers are zero-padded to 3 digits (e.g., 001, 002)
   * but can be skipped with the `skipZeroPaddedTypes` parameter.
   *
   * Inspection items with defaulted names will have the `isPartNameDefaulted` flag set to true.
   */
  setDefaultNames(): InspectionItemCollection {
    // Before we can do anything, we need to separate the items by area, then by type.
    const groupedItems = this.groupInspectionItemsByAreaAndType()

    Object.entries(groupedItems).forEach(([, itemsByType]) => {
      if (!itemsByType) return
      ;(Object.entries(itemsByType) as [InspectionItemType, InspectionItem[]][]).forEach(([itemTypeKey, items]) => {
        const defaultBaseName = INSPECTION_ITEM_DEFAULT_NAMES[itemTypeKey]
        if (!defaultBaseName) return

        items.forEach((item, index) => {
          // If the item already has a part name, skip it.
          if (item.part_name) return

          // Set the default name with zero-padded index.
          item.part_name = `${defaultBaseName}${zeroPad(index + 1, 3)}`

          // Mark the item as having a defaulted name.
          item.isPartNameDefaulted = true
        })
      })
    })

    return this
  }

  /**
   * Get an inspection item by its ID.
   *
   * @param id The inspection item ID.
   */
  getById(id: string): InspectionItem | undefined {
    return this.items.find((item) => item.inspection_item_id === id)
  }

  /**
   * Filter inspection items by their type.
   *
   * @param itemType The type of inspection item to filter by.
   */
  filterByType(itemType: InspectionItemType): InspectionItem[] {
    return this.items.filter((item) => item.item_type === itemType)
  }

  /**
   * Get inspection item by shape ID and type.
   *
   * @param shapeId The shape ID to find the inspection item for.
   * @param itemType The type of inspection item.
   */
  getByShapeId(shapeId: string, itemType: InspectionItemType): InspectionItem | undefined {
    return this.items.find((item) => item.shape_ids.polygons?.includes(shapeId) && item.item_type === itemType)
  }

  /**
   * Groups inspection items by their associated inspection area IDs and item types.
   * @param items Array of inspection items.
   * @returns A record where the keys are inspection area IDs and the values are records of inspection item types and their corresponding items.
   */
  groupInspectionItemsByAreaAndType(): Record<string, Partial<Record<InspectionItemType, InspectionItem[]>>> {
    return this.items.reduce(
      (acc, item) => {
        const areaId = item.inspection_area_id || '_unassigned_'
        if (!acc[areaId]) {
          acc[areaId] = {}
        }

        // Special case for surface plane since they're shared with volume.
        const itemType = item.item_type
        if (!itemType || (itemType === InspectionItemType.PolygonArea && item.volume_id)) return acc

        if (!acc[areaId][itemType]) {
          acc[areaId][itemType] = []
        }

        acc[areaId][itemType].push(item)
        return acc
      },
      {} as Record<string, Partial<Record<InspectionItemType, InspectionItem[]>>>,
    )
  }
}
